/**
 * EbdViewerPage — interactive viewer for Entscheidungsbaumdiagramme (EBD).
 *
 * Picks a Formatversion + EBD, fetches the raw E_####.json (and .puml) that
 * build-data.ts copied into /public/ebds, lays it out (ebdLayout) and renders
 * it as an SVG decision tree (EbdDiagram). Supports SVG + PlantUML export.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Select, SelectItem, Notice } from "@conuti-das/prince-ui";
import { Flex, Spinner } from "@/ui-prince";
import artifactsData from "@/data/generated/artifacts.json";
import type { ArtifactIndex, EbdSource } from "@/types";
import { cleanEbdName, layoutEbd } from "./ebdLayout";
import { EbdDiagram } from "./EbdDiagram";
import "./ebd.css";

const BASE = import.meta.env.BASE_URL;
const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/** "202604" → "April 2026 (FV2604)" */
function releaseLabel(release: string): string {
  const m = /^(\d{4})(\d{2})$/.exec(release);
  if (!m) return release;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]} (FV${release.slice(2)})`;
}

export function EbdViewerPage() {
  const params = useParams<{ release?: string; code?: string }>();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const data = artifactsData as ArtifactIndex;

  const releases = useMemo(
    () => Object.keys(data.ebds).filter((r) => (data.ebds[r] ?? []).length > 0).sort().reverse(),
    [data],
  );

  const [release, setRelease] = useState<string>(params.release ?? releases[0] ?? "");
  const codes = useMemo(() => data.ebds[release] ?? [], [data, release]);
  const names = useMemo(() => data.ebdNames?.[release] ?? {}, [data, release]);
  const [code, setCode] = useState<string>(params.code ?? codes[0] ?? "");

  const standIndex = useMemo(
    () => (data.ebdStaende?.[release] ?? null),
    [data, release],
  );
  const staende = useMemo(() => standIndex?.staende ?? [], [standIndex]);

  // Initialise stand from ?stand= query param if it is valid for this release's
  // index; otherwise fall back to the index's current stand.
  // A lazy initialiser runs once on mount — exactly the "init once from URL,
  // then UI-controlled" semantics we need, without an eslint-disable.
  const [stand, setStand] = useState<string>(() => {
    const fromUrl = searchParams.get("stand") ?? "";
    if (standIndex && fromUrl && standIndex.staende.some((s) => s.stand === fromUrl)) {
      return fromUrl;
    }
    return standIndex?.current ?? "";
  });

  // Beim Release-Wechsel den Stand auf 'current' zurücksetzen.
  // We track whether this is the very first render so we can skip resetting stand
  // on mount (the URL param initialisation above already set the right value).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    setStand(standIndex?.current ?? "");
  }, [standIndex]);
  const isCurrentStand = !standIndex || stand === standIndex.current;

  const [source, setSource] = useState<EbdSource | null>(null);
  const [puml, setPuml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // keep code valid when the release changes
  useEffect(() => {
    if (codes.length && !codes.includes(code)) setCode(codes[0]);
  }, [codes, code]);

  // reflect selection in the URL (shareable deep-links)
  useEffect(() => {
    if (!release || !code) return;
    // Append ?stand= only for non-current stands; current is the default → clean URL.
    const standParam = !isCurrentStand && stand ? `?stand=${stand}` : "";
    nav(`/ebd/${release}/${code}${standParam}`, { replace: true });
  }, [release, code, stand, isCurrentStand, nav]);

  // fetch the selected EBD
  useEffect(() => {
    if (!release || !code) return;
    // Stand-Index vorhanden, aber 'stand' noch nicht initialisiert (Reset-Effekt
    // läuft erst im nächsten Render) → warten, sonst spurious _staende//-Fetch.
    if (standIndex && !stand) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const base = isCurrentStand
      ? `${BASE}ebds/${release}/${code}`
      : `${BASE}ebds/${release}/_staende/${stand}/${code}`;
    Promise.all([
      fetch(`${base}.json`).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<EbdSource>;
      }),
      fetch(`${base}.puml`)
        .then((r) => (r.ok ? r.text() : null))
        // Vite/SPA-Fallback liefert für fehlende Dateien index.html mit 200 —
        // nur echte PlantUML-Quellen akzeptieren.
        .then((t) => (t && t.trimStart().startsWith("@startuml") ? t : null))
        .catch(() => null),
    ])
      .then(([json, pumlText]) => {
        if (cancelled) return;
        setSource(json);
        setPuml(pumlText);
      })
      .catch((e) => {
        if (cancelled) return;
        setSource(null);
        setPuml(null);
        setError(`EBD ${code} (${release}) konnte nicht geladen werden: ${e.message}`);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [release, code, stand, isCurrentStand, standIndex]);

  const graph = useMemo(() => (source ? layoutEbd(source) : null), [source]);

  function download(filename: string, content: string, mime: string) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportSvg() {
    if (!svgRef.current) return;
    const clone = svgRef.current.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    download(`${code}.svg`, `<?xml version="1.0" encoding="UTF-8"?>\n${xml}`, "image/svg+xml");
  }

  function exportPuml() {
    if (puml) download(`${code}.puml`, puml, "text/plain;charset=utf-8");
  }

  const instructions = source?.multi_step_instructions ?? [];

  return (
    <div className="page ebd-page">
      <div className="page-header">
        <h2>Entscheidungsbaumdiagramme</h2>
        <div className="page-subtitle">
          Optische Darstellung der EBDs aus der maco-knowledge-collection.
        </div>
      </div>

      <Flex className="ebd-toolbar" wrap="Wrap" alignItems="Center">
        <label htmlFor="ebd-fv">Formatversion</label>
        <Select
          id="ebd-fv"
          style={{ minWidth: "240px" }}
          selectedKey={release}
          onSelectionChange={(key) => setRelease(String(key))}
        >
          {releases.map((r) => (
            <SelectItem key={r} id={r}>
              {releaseLabel(r)}
            </SelectItem>
          ))}
        </Select>

        {staende.length > 0 && (
          <>
            <label htmlFor="ebd-stand">Stand</label>
            <Select
              id="ebd-stand"
              style={{ minWidth: "240px" }}
              selectedKey={stand}
              onSelectionChange={(key) => setStand(String(key))}
            >
              {staende.map((s) => (
                <SelectItem key={s.stand} id={s.stand}>
                  {s.stand === standIndex!.current ? `${s.label} (aktuell)` : s.label}
                </SelectItem>
              ))}
            </Select>
          </>
        )}

        <label htmlFor="ebd-code">EBD</label>
        <Select
          id="ebd-code"
          style={{ minWidth: "380px" }}
          selectedKey={code}
          onSelectionChange={(key) => setCode(String(key))}
        >
          {codes.map((c) => {
            const name = cleanEbdName(names[c]);
            return (
              <SelectItem key={c} id={c}>
                {name ? `${c} — ${name}` : c}
              </SelectItem>
            );
          })}
        </Select>

        <div className="ebd-toolbar-spacer" />

        <Button
          variant="plain"
          isDisabled={!graph}
          onPress={exportSvg}
        >
          Export SVG
        </Button>
        <Button
          variant="plain"
          isDisabled={!puml}
          onPress={exportPuml}
        >
          Export PUML
        </Button>
      </Flex>

      {error && (
        <div style={{ marginBottom: "var(--s-3)" }}>
          <Notice tone="negative">{error}</Notice>
        </div>
      )}

      {instructions.length > 0 && (
        <div style={{ marginBottom: "var(--s-3)" }}>
          <Notice tone="info">
            {instructions
              .map((i) => `Ab Schritt ${i.first_step_number_affected}: ${i.instruction_text}`)
              .join("  •  ")}
          </Notice>
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)", margin: "var(--s-4)" }}>
          <Spinner active size="m" />
          <span>Lade EBD …</span>
        </div>
      )}

      {!loading && graph && <EbdDiagram ref={svgRef} graph={graph} />}

      {!loading && !graph && !error && (
        <Notice tone="info">
          Wähle eine Formatversion und ein EBD.
        </Notice>
      )}
    </div>
  );
}
